import {
  Component,
  EventEmitter,
  Output,
  inject,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';

import { InputComponent, IconComponent, SelectComponent } from '../../../../../@ads/components';
import { CreateAccountPjFormHandler } from '../../create-account-pj-form.handler';
import { strictEmailValidator } from '../../../../utils/email.validator';

const RELACAO_OPTIONS = [
  { value: 'socio-proprietario', label: 'Sócio-proprietário' },
  { value: 'diretor', label: 'Diretor' },
  { value: 'procurador', label: 'Procurador' },
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'presidente', label: 'Presidente' },
  { value: 'outra', label: 'Outra' },
];

function cpfFormatValidator(control: FormControl): { [key: string]: any } | null {
  const raw = control.value?.replace(/\D/g, '') ?? '';
  if (!raw) return null;
  return cpfValidator.isValid(raw) ? null : { cpfInvalid: true };
}

@Component({
  selector: 'app-representative-form',
  standalone: true,
  imports: [ReactiveFormsModule, InputComponent, IconComponent, SelectComponent],
  templateUrl: './representative-form.component.html',
  styleUrl: './representative-form.component.scss',
})
export class RepresentativeFormComponent implements OnInit, OnDestroy {
  private readonly _handler = inject(CreateAccountPjFormHandler);
  private readonly _destroy$ = new Subject<void>();
  private readonly _rowIds = new WeakMap<FormGroup, number>();
  private _nextRowId = 0;

  @Output() setStepValidation = new EventEmitter<boolean>();

  public readonly relacaoOptions = RELACAO_OPTIONS;

  public readonly errorMessages = {
    required: 'Campo obrigatório',
    email: 'E-mail inválido',
    cpfInvalid: 'CPF inválido',
    cpfDuplicate: 'CPF já adicionado nesta empresa',
  };

  public form = new FormGroup({
    representatives: new FormArray<FormGroup>([]),
  });

  get representatives(): FormArray {
    return this.form.get('representatives') as FormArray;
  }

  ngOnInit(): void {
    const saved = this._handler.getRepresentatives();
    if (saved.length > 0) {
      saved.forEach((rep) => this.addRepresentative(rep));
    } else {
      this.addRepresentative();
    }

    this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.validateDuplicates();
      this.setStepValidation.emit(this.form.valid);
      if (this.form.valid) {
        const reps = this.representatives.controls.map((g) => {
          const val = (g as FormGroup).getRawValue();
          return {
            cpf: val.cpf.replace(/\D/g, ''),
            nome: val.nome,
            email: val.email,
            relacao: val.relacao !== 'outra' ? val.relacao : undefined,
            outraRelacao: val.relacao === 'outra' ? val.outraRelacao : undefined,
          };
        });
        this._handler.setRepresentatives(reps);
      }
    });

    this.setStepValidation.emit(this.form.valid);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  addRepresentative(data?: any): void {
    const group = new FormGroup({
      cpf: new FormControl(data?.cpf ?? '', {
        nonNullable: true,
        validators: [Validators.required, cpfFormatValidator as any],
      }),
      nome: new FormControl(data?.nome ?? '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)],
      }),
      email: new FormControl(data?.email ?? '', {
        nonNullable: true,
        validators: [Validators.required, strictEmailValidator],
      }),
      relacao: new FormControl(data?.relacao ?? (data?.outraRelacao ? 'outra' : ''), {
        nonNullable: true,
        validators: [Validators.required],
      }),
      outraRelacao: new FormControl(data?.outraRelacao ?? '', {
        nonNullable: true,
      }),
    });

    this._rowIds.set(group, this._nextRowId++);
    this.representatives.push(group);
  }

  /** Identifica a linha para testes: não muda quando outra linha é removida e não expõe o CPF. */
  rowId(index: number): number | undefined {
    return this._rowIds.get(this.representatives.at(index) as FormGroup);
  }

  isVerified(index: number): boolean {
    return this._handler.isVerifiedRepresentative(this.representatives.at(index).getRawValue().cpf);
  }

  removeRepresentative(index: number): void {
    if (this.representatives.length > 1 && !this.isVerified(index)) {
      this.representatives.removeAt(index);
      this.validateDuplicates();
      this.setStepValidation.emit(this.form.valid);
    }
  }

  isRelacaoOutra(index: number): boolean {
    const group = this.representatives.at(index) as FormGroup;
    return group.get('relacao')?.value === 'outra';
  }

  private validateDuplicates(): void {
    const cpfs: string[] = [];
    this.representatives.controls.forEach((ctrl) => {
      const group = ctrl as FormGroup;
      const raw = group.get('cpf')?.value?.replace(/\D/g, '') ?? '';

      // Check CPF duplicates
      const cpfCtrl = group.get('cpf');
      if (raw && cpfs.includes(raw)) {
        cpfCtrl?.setErrors({ cpfDuplicate: true });
      } else if (cpfCtrl?.errors?.['cpfDuplicate']) {
        const { cpfDuplicate: _, ...rest } = cpfCtrl.errors ?? {};
        cpfCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }

      if (raw) cpfs.push(raw);
    });
  }
}
